import React, { useState } from 'react';
import { GitBranch, ChevronDown, CheckCircle2, XCircle, Clock, AlertTriangle, Send, Archive, PauseCircle, RotateCcw, Edit2, Share2, UserMinus } from 'lucide-react';
import { TODAY } from '@/data/financial-systems';
import { COMPREHENSIVE_SCENARIOS } from '@/data/scenario-data';
import type { ScenarioAuditEntry, ScenarioAuditEventType } from '@/types/scenarios';

const fmtDateTime = (d: Date) =>
  d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

type EventFilter = ScenarioAuditEventType | 'all';

interface FlatEntry extends ScenarioAuditEntry {
  scenarioName: string;
}

const WORKFLOW_EVENTS: ScenarioAuditEventType[] = [
  'submitted-for-approval',
  'approval-granted',
  'approval-rejected',
  'approval-withdrawn',
  'changes-requested',
  'reviewed',
  'committed',
  'parked',
  'reactivated',
  'archived',
];

const eventConfig: Record<ScenarioAuditEventType, { label: string; icon: React.ReactNode; colour: string }> = {
  'created':              { label: 'Created',              icon: <Edit2 className="h-3.5 w-3.5" />,         colour: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' },
  'edited':               { label: 'Edited',               icon: <Edit2 className="h-3.5 w-3.5" />,         colour: 'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-950/30 dark:border-slate-800' },
  'shared':               { label: 'Shared',               icon: <Share2 className="h-3.5 w-3.5" />,        colour: 'text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800' },
  'unshared':             { label: 'Access Removed',       icon: <UserMinus className="h-3.5 w-3.5" />,     colour: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800' },
  'submitted-for-approval': { label: 'Submitted',          icon: <Send className="h-3.5 w-3.5" />,          colour: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' },
  'approval-granted':     { label: 'Approved',             icon: <CheckCircle2 className="h-3.5 w-3.5" />,  colour: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800' },
  'approval-rejected':    { label: 'Rejected',             icon: <XCircle className="h-3.5 w-3.5" />,       colour: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800' },
  'approval-withdrawn':   { label: 'Withdrawn',            icon: <RotateCcw className="h-3.5 w-3.5" />,     colour: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800' },
  'changes-requested':    { label: 'Changes Requested',    icon: <AlertTriangle className="h-3.5 w-3.5" />, colour: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800' },
  'reviewed':             { label: 'Reviewed',             icon: <CheckCircle2 className="h-3.5 w-3.5" />,  colour: 'text-teal-600 bg-teal-50 border-teal-200 dark:bg-teal-950/30 dark:border-teal-800' },
  'committed':            { label: 'Committed',            icon: <CheckCircle2 className="h-3.5 w-3.5" />,  colour: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800' },
  'parked':               { label: 'Parked',               icon: <PauseCircle className="h-3.5 w-3.5" />,   colour: 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800' },
  'reactivated':          { label: 'Reactivated',          icon: <RotateCcw className="h-3.5 w-3.5" />,     colour: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' },
  'archived':             { label: 'Archived',             icon: <Archive className="h-3.5 w-3.5" />,       colour: 'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-950/30 dark:border-slate-800' },
  'status-changed':       { label: 'Status Changed',       icon: <Clock className="h-3.5 w-3.5" />,         colour: 'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-950/30 dark:border-slate-800' },
};

// Flatten all scenario audit logs into a single sorted list, workflow events only
function buildWorkflowEntries(): FlatEntry[] {
  const entries: FlatEntry[] = [];
  for (const scenario of COMPREHENSIVE_SCENARIOS) {
    for (const entry of (scenario.auditLog ?? [])) {
      if (WORKFLOW_EVENTS.includes(entry.eventType)) {
        entries.push({ ...entry, scenarioName: scenario.name });
      }
    }
  }
  return entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', 'under-review': 'Under Review', 'pending-approval': 'Pending Approval',
  active: 'Active', committed: 'Committed', parked: 'Parked', archived: 'Archived', rejected: 'Rejected',
};

export const ScenarioWorkflowAuditPreview: React.FC = () => {
  const [filter, setFilter] = useState<EventFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const reportDate = TODAY.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const allEntries = buildWorkflowEntries();
  const filtered = filter === 'all' ? allEntries : allEntries.filter(e => e.eventType === filter);

  // Counts per event type
  const counts = WORKFLOW_EVENTS.reduce<Record<string, number>>((acc, evt) => {
    acc[evt] = allEntries.filter(e => e.eventType === evt).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Scenario Workflow Audit</h3>
          <p className="text-sm text-muted-foreground">BuildRight Construction Ltd · All scenarios · Approval and status lifecycle events</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles — key workflow events */}
      <div className="grid grid-cols-5 gap-2">
        {(['submitted-for-approval', 'approval-granted', 'approval-rejected', 'changes-requested', 'committed'] as ScenarioAuditEventType[]).map(evt => {
          const cfg = eventConfig[evt];
          return (
            <button
              key={evt}
              onClick={() => setFilter(filter === evt ? 'all' : evt)}
              className={`rounded-lg border p-3 text-center transition-all hover:opacity-80 ${filter === evt ? cfg.colour : 'bg-muted/40 border-muted'}`}
            >
              <div className={`flex justify-center mb-1 ${filter === evt ? '' : 'text-muted-foreground'}`}>{cfg.icon}</div>
              <p className="text-lg font-bold">{counts[evt] ?? 0}</p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">{cfg.label}</p>
            </button>
          );
        })}
      </div>

      {filter !== 'all' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtered by:</span>
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${eventConfig[filter].colour}`}>
            {eventConfig[filter].icon}
            {eventConfig[filter].label}
          </span>
          <button onClick={() => setFilter('all')} className="text-xs text-muted-foreground underline hover:no-underline">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date / Time</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Scenario</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Actor</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Event</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Stage</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">From → To</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(entry => {
              const cfg = eventConfig[entry.eventType];
              const isExpanded = expandedId === entry.id;
              return (
                <React.Fragment key={entry.id}>
                  <tr className={`hover:bg-muted/30 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDateTime(entry.timestamp)}</td>
                    <td className="px-4 py-2.5 font-medium max-w-[180px] truncate">{entry.scenarioName}</td>
                    <td className="px-4 py-2.5">{entry.actorName}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.colour}`}>
                        {cfg.icon}
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">
                      {entry.stageName ? (
                        <span>{entry.stageOrder ? `Stage ${entry.stageOrder}: ` : ''}{entry.stageName}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {entry.fromStatus && entry.toStatus ? (
                        <span className="flex items-center gap-1">
                          <span className="font-medium">{STATUS_LABELS[entry.fromStatus] ?? entry.fromStatus}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-medium">{STATUS_LABELS[entry.toStatus] ?? entry.toStatus}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      {entry.detail && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Detail
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && entry.detail && (
                    <tr className="bg-muted/20">
                      <td colSpan={7} className="px-4 py-2.5 text-sm text-muted-foreground italic border-l-2 border-primary/30">
                        {entry.detail}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No workflow events found for the selected filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-4">
        <GitBranch className="h-3.5 w-3.5" />
        <span>{allEntries.length} total workflow events across {COMPREHENSIVE_SCENARIOS.filter(s => (s.auditLog ?? []).some(e => WORKFLOW_EVENTS.includes(e.eventType))).length} scenarios</span>
      </div>
    </div>
  );
};
